// ─── Credit Card Reconciliation — API Wrappers ────────────────────────────────
// Shares the authenticated axios instance (and MSAL token interceptor) from
// adminApi.ts.  All endpoints require the POWER_USER or ADMIN Entra role.
//
// Backend routes: /api/cc-recon/*
// Reference: CLAUDE_API.md → CC Recon Endpoints

import { API } from "../adminApi";
import type {
  CCReconSession,
  CCReconTransaction,
  CCHelperMapping,
  CCCreateSessionResponse,
  CCGetSessionResponse,
  CCConvertAllResponse,
  CCTransactionUpdate,
  CCHelperMappingUpsert,
  CCMatchesResponse,
  CCFindMatchesResponse,
  CreditCard,
} from "../types/ccTypes";

// ── Sessions ──────────────────────────────────────────────────────────────────

/** List all CC recon sessions, newest first. */
export async function listSessions(): Promise<CCReconSession[]> {
  const { data } = await API.get("/cc-recon/sessions");
  return data;
}

/**
 * Upload a Chase CSV and create a new session.
 * Reads the File to ArrayBuffer, converts to base64, and sends as JSON so the
 * Azure Functions backend receives a plain JSON body (same pattern as
 * process-invoice).
 *
 * @param ccVendorNumber  WinTeam vendor number for the card this CSV belongs to.
 *                        Stored on the session and used as creditCardVendorNumber
 *                        when converting transactions to WinTeam invoices.
 */
export async function createSession(
  sessionName: string,
  file: File,
  ccVendorNumber: number,
): Promise<CCCreateSessionResponse> {
  const arrayBuffer = await file.arrayBuffer();
  const uint8 = new Uint8Array(arrayBuffer);
  let binary = "";
  for (let i = 0; i < uint8.length; i++) {
    binary += String.fromCharCode(uint8[i]);
  }
  const fileDataB64 = btoa(binary);

  const { data } = await API.post("/cc-recon/sessions", {
    session_name: sessionName,
    file_data: fileDataB64,
    file_name: file.name,
    cc_vendor_number: ccVendorNumber,
  });
  return data;
}

/**
 * List active credit cards from ap_credit_cards.
 * Used to populate the card selector in the upload modal.
 */
export async function listCreditCards(): Promise<CreditCard[]> {
  const { data } = await API.get("/credit-cards");
  return data;
}

/**
 * Fetch a single session and all its transactions.
 */
export async function getSession(sessionId: number): Promise<CCGetSessionResponse> {
  const { data } = await API.get(`/cc-recon/sessions/${sessionId}`);
  return data;
}

/**
 * Rename a session (session_name only; status is changed via completeSession).
 */
export async function updateSession(
  sessionId: number,
  sessionName: string
): Promise<CCReconSession> {
  const { data } = await API.patch(`/cc-recon/sessions/${sessionId}`, {
    session_name: sessionName,
  });
  return data;
}

/**
 * Patch any subset of a session's reconciliation fields:
 *   beginning_balance / ending_balance / finance_charge / fee_charged (number|null),
 *   invoice_seq_start (integer >= 1; rejected with 409 once any row is converted).
 * Balances/charges are editable on open AND complete sessions.
 */
export async function updateSessionFields(
  sessionId: number,
  fields: {
    beginning_balance?: number | null;
    ending_balance?: number | null;
    finance_charge?: number | null;
    fee_charged?: number | null;
    invoice_seq_start?: number;
  }
): Promise<CCReconSession> {
  const { data } = await API.patch(`/cc-recon/sessions/${sessionId}`, fields);
  return data;
}

/** @deprecated use updateSessionFields. Kept for back-compat. */
export async function updateSessionBalances(
  sessionId: number,
  balances: { beginning_balance?: number | null; ending_balance?: number | null }
): Promise<CCReconSession> {
  return updateSessionFields(sessionId, balances);
}

/**
 * Generate the reconciliation PDF report for a session and return a blob
 * object URL (suitable for window.open). Caller is responsible for revoking it.
 * Works for both open and complete sessions.
 */
export async function printReconReport(sessionId: number): Promise<string> {
  const res = await API.get(`/cc-recon/sessions/${sessionId}/report`, {
    responseType: "blob",
  });
  const blob = new Blob([res.data], { type: "application/pdf" });
  return URL.createObjectURL(blob);
}

/**
 * Mark a session as complete (locks it).
 * Backend will reject if any Unresolved or Error rows remain.
 */
export async function completeSession(sessionId: number): Promise<CCReconSession> {
  const { data } = await API.post(`/cc-recon/sessions/${sessionId}/complete`);
  return data;
}

/**
 * Delete a session and all its transactions.
 * Only open sessions may be deleted — backend rejects completed sessions with 409.
 * Returns void (204 No Content from backend).
 */
export async function deleteSession(sessionId: number): Promise<void> {
  await API.delete(`/cc-recon/sessions/${sessionId}`);
}

// ── Transactions ──────────────────────────────────────────────────────────────

/**
 * Inline-edit a transaction (vendor_number, gl_number, job_number, notes).
 * Backend sets resolution_source → 'Manual' and status → 'Corrected'.
 * Returns 409 if the transaction is locked (Converted / Confirmed Match / Duplicate).
 */
export async function updateTransaction(
  transactionId: number,
  updates: CCTransactionUpdate
): Promise<CCReconTransaction> {
  const { data } = await API.patch(
    `/cc-recon/transactions/${transactionId}`,
    updates
  );
  return data;
}

/**
 * Convert a single transaction to a WinTeam invoice (POST to WinTeam API).
 * Returns the created invoice number on success.
 * Returns HTTP 501 until cc_poster.py is deployed (Step 8).
 */
export async function convertTransaction(
  transactionId: number
): Promise<CCReconTransaction> {
  const { data } = await API.post(
    `/cc-recon/transactions/${transactionId}/convert`
  );
  return data;
}

/**
 * Convert all eligible transactions in a session in one call.
 * Skips transactions in CC_BLOCKED_STATUSES (Unresolved, Possible Match,
 * Confirmed Match, Converted, Duplicate).
 */
export async function convertAll(
  sessionId: number
): Promise<CCConvertAllResponse> {
  const { data } = await API.post(
    `/cc-recon/sessions/${sessionId}/convert-all`
  );
  return data;
}

// ── Possible Match ────────────────────────────────────────────────────────────

/**
 * Run AI Assist (Tier 4) on all Unresolved transactions in a session.
 *
 * Phase 2 of the two-phase upload architecture (Session 56):
 *   Phase 1 — createSession()      : tiers 1-3 only (fast, ~5s)
 *   Phase 2 — runAiResolution()    : AI for remaining Unresolved rows (~30-90s)
 *
 * Called automatically by CreditCardRecon after upload if any Unresolved
 * transactions remain. Safe to call again — skips already-resolved rows.
 */
export async function runAiResolution(sessionId: number): Promise<{
  processed: number;
  resolved: number;
  still_unresolved: number;
}> {
  const { data } = await API.post(`/cc-recon/sessions/${sessionId}/run-ai`);
  return data;
}

/**
 * Run the WinTeam Possible Match search for all eligible transactions in a
 * session. Marks matching transactions as 'Possible Match'.
 * Returns { checked: N, updated: M }.
 */
export async function findSessionMatches(
  sessionId: number
): Promise<CCFindMatchesResponse> {
  const { data } = await API.post(`/cc-recon/sessions/${sessionId}/find-matches`);
  return data;
}

/**
 * Fetch WinTeam invoice candidates for a single transaction.
 * Re-queries WinTeam each time — use when opening the review panel.
 */
export async function getTransactionMatches(
  transactionId: number
): Promise<CCMatchesResponse> {
  const { data } = await API.get(`/cc-recon/transactions/${transactionId}/matches`);
  return data;
}

/**
 * Confirm a specific WinTeam invoice as a match for this transaction.
 * Sets status → 'Confirmed Match', matched_invoice_number → invoiceNumber.
 */
export async function confirmMatch(
  transactionId: number,
  invoiceNumber: string
): Promise<CCReconTransaction> {
  const { data } = await API.post(
    `/cc-recon/transactions/${transactionId}/confirm-match`,
    { invoice_number: invoiceNumber }
  );
  return data;
}

/**
 * Reject the Possible Match — reverts transaction to 'Coded' or 'Unresolved'.
 */
export async function rejectMatch(
  transactionId: number
): Promise<CCReconTransaction> {
  const { data } = await API.post(
    `/cc-recon/transactions/${transactionId}/reject-match`
  );
  return data;
}

// ── Admin Force-Status ────────────────────────────────────────────────────────

/**
 * Admin-only: force a transaction to any valid status, bypassing normal rules.
 * Use to recover stuck Error/Converted rows without going to SQL.
 * @param notes  Optional reason shown in the notes field on the transaction.
 */
export async function forceTransactionStatus(
  transactionId: number,
  status: string,
  notes?: string
): Promise<CCReconTransaction> {
  const { data } = await API.post(
    `/cc-recon/transactions/${transactionId}/force-status`,
    { status, notes: notes || null }
  );
  return data;
}

// ── CC Helper Mappings ────────────────────────────────────────────────────────

/**
 * List all CC Helper mappings (sorted by match_count DESC).
 */
export async function listHelperMappings(): Promise<CCHelperMapping[]> {
  const { data } = await API.get("/cc-recon/helper-mappings");
  return data;
}

/**
 * Upsert a CC Helper mapping.
 * ON CONFLICT (description_pattern) updates vendor_number, gl_number,
 * job_number, source, and increments match_count.
 */
export async function upsertHelperMapping(
  mapping: CCHelperMappingUpsert
): Promise<CCHelperMapping> {
  const { data } = await API.post("/cc-recon/helper-mappings", mapping);
  return data;
}
